import React from 'react';
import _ from 'lodash';
import Promise from 'bluebird';
import apiFetch from '@scale/frontend-lib/api-fetch';
import Alert from 'react-scale/catalog/components/Alert';

export const requiredAsterisk = (
  <span style={{ color: 'var(--color-RemoRed)', fontWeight: 'bold' }}>*</span>
);

const silentlyLogError = (err: any) => err.logError && err.logError('corp lookup');

const fetchAndSetState = async (fetchPromise: $FIXME) => {
  const value = await fetchPromise.catch(silentlyLogError);
  return value;
};

function readCorpKeyValue(flag: string, key: string): string | null {
  return localStorage.getItem([flag, key].join('/'));
}
interface IFetchExtrasProps {
  type: string;
  id: string;
  urlId: string;
  permissionGroupId: string;
}

export async function fetchExtras(props: IFetchExtrasProps) {
  const { type, id, urlId, permissionGroupId } = props;

  const result: Record<string, any> = {};
  if (type === 'project') {
    const corpLookupScratchPad = readCorpKeyValue('corp-lookup-scratchpad', id);

    result.corpLookupScratchPad = corpLookupScratchPad;
    const owners = await apiFetch(null, `corp-api/lookup/${urlId}/owners`, {
      method: 'GET',
    }).catch(silentlyLogError);

    result.owners = owners;
    const { instructionCategories } = await Promise.props(
      _.mapValues(
        {
          recentTaskers: apiFetch(null, `corp-api/lookup/${urlId}/recentTaskers`, {
            method: 'GET',
          }),
          recentReviewers: apiFetch(null, `corp-api/lookup/${urlId}/recentReviewers`, {
            method: 'GET',
          }),
          instructionCategories: apiFetch(null, `internal/instructions/all/${id}`, {
            method: 'GET',
          }),
          weeklyProgress: apiFetch(null, `corp-api/lookup/${urlId}/weeklyProgress`, {
            method: 'GET',
          }),
          unclaimedSubtasks: apiFetch(null, `corp-api/lookup/${urlId}/unclaimedSubtasks`, {
            method: 'GET',
          }),
          recentCompletedSubtasks: apiFetch(
            null,
            `corp-api/lookup/${urlId}/recentCompletedSubtasks`,
            { method: 'GET' },
          ),
          recentCompletedTasks: apiFetch(null, `corp-api/lookup/${urlId}/recentCompletedTasks`, {
            method: 'GET',
          }),
          recentPendingSubtasks: apiFetch(null, `corp-api/lookup/${urlId}/recentPendingSubtasks`, {
            method: 'GET',
          }),
          recentPendingTasks: apiFetch(null, `corp-api/lookup/${urlId}/recentPendingTasks`, {
            method: 'GET',
          }),
          recentErroredTasks: apiFetch(null, `corp-api/lookup/${urlId}/recentErroredTasks`, {
            method: 'GET',
          }),
          recentSubmittedTrainingAttempts: apiFetch(
            null,
            `corp-api/lookup/${urlId}/recentSubmittedTrainingAttempts`,
            {
              method: 'GET',
            },
          ),
          recentAttempterBenchmarks: apiFetch(
            null,
            `corp-api/lookup/${urlId}/recentAttempterBenchmarks`,
            { method: 'GET' },
          ),
          recentReviewerBenchmarks: apiFetch(
            null,
            `corp-api/lookup/${urlId}/recentReviewerBenchmarks`,
            { method: 'GET' },
          ),
          permissions: apiFetch(null, `corp-api/permissions/byGroupID/${permissionGroupId}`, {
            method: 'GET',
          }),
        },
        async (p, key) => {
          const value = await fetchAndSetState(p);
          result[key] = value;
          return value;
        },
      ),
    );

    await Promise.all(
      _.map(instructionCategories, async (c, cIndex) => {
        const benchmarks = apiFetch(null, `internal/instructions/${c._id}/benchmarks`, {
          method: 'GET',
        });

        instructionCategories[cIndex].benchmarks = benchmarks;
        result.instructionCategories = instructionCategories;
      }),
    );
  } else if (type === 'trainingscenario') {
    const recentSubmittedTrainingAttempts = await apiFetch(
      null,
      `corp-api/lookup/${urlId}/recentSubmittedTrainingAttempts`,
    ).catch(silentlyLogError);
    result.recentSubmittedTrainingAttempts = recentSubmittedTrainingAttempts;
  } else if (type === 'taskscenedefinition') {
    const recentTaskScenes = await apiFetch(
      null,
      `corp-api/lookup/${urlId}/recentTaskScenes`,
    ).catch(silentlyLogError);

    result.recentTaskScenes = recentTaskScenes;
  } else if (type === 'taskattempt') {
    const reviewFixDetails = await apiFetch(
      null,
      `corp-api/lookup/${urlId}/reviewfixes_details`,
    ).catch(silentlyLogError);

    result.reviewFixDetails = reviewFixDetails;
  } else if (type === 'taskreview') {
    const reviewFixDetails = await apiFetch(
      null,
      `corp-api/lookup/${urlId}/taskreviews_details`,
    ).catch(silentlyLogError);

    result.reviewFixDetails = reviewFixDetails;
  } else if (type === 'selecttable') {
    const selectTableStats = await apiFetch(null, `corp-api/select/lookup/${id}/stats`).catch(
      silentlyLogError,
    );

    result.selectTableStats = selectTableStats;
  } else {
    result.reviewFixDetails = null;
  }

  return result;
}

interface LayoutWithAlertProps {
  children: React.ReactNode;
}

export const LayoutWithAlert: React.FC<LayoutWithAlertProps> = ({ children }) => {
  return (
    <>
      <Alert
        style={{
          position: 'absolute',
          top: '110px',
          right: '20px',
          zIndex: 1,
        }}
        severity="error"
      >
        This tool is marked to be deprecated. <br /> If you would like to keep using this tool,
        <br /> please reach out to celeste.colamarino@scale.com
        <br /> or on slack at #corp-cleanup
      </Alert>
      {children}
    </>
  );
};

export const DCT_DEPRECATION_BANNER_LIST = [
  'update-long-hints',
  'update-active-training-program',
  'catalog-brand-db',
  'catalog-explorer-replace-image-links',
  'catalog-generate-training-data-tasks',
  'catalog-run-training-data-model',
  'catalog-start-model-train',
  'catalog-test-train-split',
  'catalog-train-i2a-model',
  'generate-dataset-payments',
  'outlier-worker-team-mirror',
  'partial-sbq-textcollection',
  'permission-taskers-and-forever-claim-subtasks',
  'personalized-courses',
  'send-autoflag-tasks',
  'promote-or-demote-taskers',
  'provision-taskers',
  'qir-update-flamingo-bullet-tasks',
  'regrade-bee-audits',
  'rerun-topdown-task-processor',
  'retroactive-enable-disable',
  'update-fancy-glacier-rat-attachments-prompts',
  'update-billing-type',
];
